<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import Button from "@/components/Base/Button";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import { Menu } from "@/components/Base/Headless";

export type AppDataTableColumn = {
  key: string;
  label: string;
  visible?: boolean;
  /** Por defecto: true salvo columna `actions` */
  sortable?: boolean;
  /** Por defecto: igual que sortable */
  filterable?: boolean;
  sortValue?: (row: Record<string, unknown>) => string | number | null | undefined;
  filterValue?: (row: Record<string, unknown>) => string | number | null | undefined;
};

type Props = {
  columns: AppDataTableColumn[];
  data: Record<string, unknown>[];
  emptyMessage?: string;
  enableSearch?: boolean;
  searchQuery?: string;
  disableClientSearch?: boolean;
  /** Filtro por columna sobre los datos ya cargados en la tabla */
  enableTableFilter?: boolean;
  enableColumnManager?: boolean;
  enableExport?: boolean;
  maxHeight?: string;
};

const props = withDefaults(defineProps<Props>(), {
  emptyMessage: undefined,
  enableSearch: true,
  enableTableFilter: true,
  enableColumnManager: true,
  enableExport: true,
});

const emit = defineEmits(["update:searchQuery", "search", "sort"]);

const { t } = useI18n();

const resolvedEmptyMessage = computed(
  () => props.emptyMessage ?? t("common.no_data")
);

const internalSearchQuery = ref(props.searchQuery || "");
const sortBy = ref<string | null>(null);
const sortOrder = ref<"asc" | "desc" | null>(null);
const tableFilterField = ref("__all__");
const tableFilterValue = ref("");

const internalColumns = ref(
  props.columns.map((c) => normalizeColumn(c))
);

function isActionsColumn(key: string) {
  return key === "actions";
}

function normalizeColumn(c: AppDataTableColumn) {
  const sortable = c.sortable ?? !isActionsColumn(c.key);
  return {
    ...c,
    visible: c.visible !== false,
    sortable,
    filterable: c.filterable ?? sortable,
  };
}

watch(
  () => props.columns,
  (newCols) => {
    internalColumns.value = newCols.map((c) => {
      const existing = internalColumns.value.find((ic) => ic.key === c.key);
      const normalized = normalizeColumn(c);
      return {
        ...normalized,
        visible: existing ? existing.visible : normalized.visible,
      };
    });
  },
  { deep: true }
);

watch(
  () => props.searchQuery,
  (newVal) => {
    if (newVal !== undefined) internalSearchQuery.value = newVal;
  }
);

watch(internalSearchQuery, (newVal) => {
  emit("update:searchQuery", newVal);
});

const visibleColumns = computed(() => internalColumns.value.filter((c) => c.visible));

const filterableColumns = computed(() =>
  internalColumns.value.filter((c) => c.filterable)
);

function getColumn(key: string) {
  return internalColumns.value.find((c) => c.key === key);
}

function cellText(
  row: Record<string, unknown>,
  col: AppDataTableColumn & { sortable?: boolean; filterable?: boolean }
): string {
  const getter = col.filterValue ?? col.sortValue;
  let raw: unknown;
  if (getter) {
    raw = getter(row);
  } else {
    raw = row?.[col.key];
    if (raw !== null && typeof raw === "object") {
      raw = JSON.stringify(raw);
    }
  }
  if (raw === null || raw === undefined) return "";
  return String(raw).toLowerCase();
}

function compareCellValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  order: "asc" | "desc"
): number {
  const emptyA = a === null || a === undefined || a === "";
  const emptyB = b === null || b === undefined || b === "";
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  const numA = typeof a === "number" ? a : Number(a);
  const numB = typeof b === "number" ? b : Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB) && String(a).trim() !== "" && String(b).trim() !== "") {
    const diff = numA - numB;
    return order === "asc" ? diff : -diff;
  }

  const strA = String(a);
  const strB = String(b);
  const dateA = Date.parse(strA);
  const dateB = Date.parse(strB);
  if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && /^\d{4}-\d{2}/.test(strA) && /^\d{4}-\d{2}/.test(strB)) {
    const diff = dateA - dateB;
    return order === "asc" ? diff : -diff;
  }

  const cmp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" });
  return order === "asc" ? cmp : -cmp;
}

const filteredData = computed(() => {
  let result = [...props.data];

  if (!props.disableClientSearch && internalSearchQuery.value.trim()) {
    const lowerQuery = internalSearchQuery.value.toLowerCase().trim();
    result = result.filter((row) =>
      visibleColumns.value.some((col) => cellText(row, col).includes(lowerQuery))
    );
  }

  if (props.enableTableFilter && tableFilterValue.value.trim()) {
    const term = tableFilterValue.value.toLowerCase().trim();
    const field = tableFilterField.value;

    result = result.filter((row) => {
      if (field === "__all__") {
        return filterableColumns.value.some((col) => cellText(row, col).includes(term));
      }
      const col = getColumn(field);
      if (!col) return true;
      return cellText(row, col).includes(term);
    });
  }

  if (sortBy.value && sortOrder.value) {
    const col = getColumn(sortBy.value);
    if (col?.sortable) {
      const order = sortOrder.value;
      result.sort((a, b) => {
        const getter = col.sortValue ?? col.filterValue;
        const valA = getter ? getter(a) : a[col.key];
        const valB = getter ? getter(b) : b[col.key];
        return compareCellValues(
          valA as string | number | null | undefined,
          valB as string | number | null | undefined,
          order
        );
      });
    }
  }

  return result;
});

const onSort = (key: string) => {
  const col = getColumn(key);
  if (!col?.sortable) return;

  if (sortBy.value === key) {
    if (sortOrder.value === "asc") {
      sortOrder.value = "desc";
    } else if (sortOrder.value === "desc") {
      sortBy.value = null;
      sortOrder.value = null;
    }
  } else {
    sortBy.value = key;
    sortOrder.value = "asc";
  }
  emit("sort", { key: sortBy.value, order: sortOrder.value });
};

const onSearchInput = () => {
  emit("search", internalSearchQuery.value);
};

const resetTableFilter = () => {
  tableFilterField.value = "__all__";
  tableFilterValue.value = "";
};

const toggleColumn = (key: string) => {
  const col = internalColumns.value.find((c) => c.key === key);
  if (col) col.visible = !col.visible;
};

const onExportCsv = () => {
  const header = visibleColumns.value.map((c) => c.label).join(",");
  const rows = filteredData.value.map((row) =>
    visibleColumns.value
      .map((c) => {
        const text = cellText(row, c) || String(row?.[c.key] ?? "");
        return `"${text.replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "export_data.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const onExportXlsx = async () => {
  try {
    const XLSX = await import("xlsx");
    const exportData = filteredData.value.map((row) =>
      Object.fromEntries(
          visibleColumns.value.map((c) => [c.label, cellText(row, c) || (row?.[c.key] ?? "")])
      )
    );
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "export_data.xlsx");
  } catch (e) {
    console.error("No se pudo cargar XLSX para exportar a Excel", e);
    alert(t("common.export_error"));
  }
};

const onPrint = () => {
  window.print();
};
</script>

<template>
  <div class="space-y-3 w-full">
    <div class="flex flex-col sm:flex-row gap-x-3 gap-y-2 items-center justify-between mb-3 w-full">
      <div class="flex items-center gap-3">
        <div v-if="enableSearch" class="relative">
          <Lucide icon="Search" class="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
          <FormInput
            v-model="internalSearchQuery"
            type="text"
            class="pl-9 w-full sm:w-64"
            :placeholder="t('common.table_search_placeholder')"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="flex items-center gap-2 flex-wrap">
        <Menu v-if="enableColumnManager">
          <Menu.Button :as="Button" variant="outline-secondary" class="bg-white">
            <Lucide icon="Columns" class="stroke-[1.3] w-4 h-4 mr-2" />
            {{ t("common.columns") }}
            <Lucide icon="ChevronDown" class="stroke-[1.3] w-4 h-4 ml-2" />
          </Menu.Button>
          <Menu.Items class="w-56 shadow-md z-50">
            <Menu.Item
              v-for="col in internalColumns"
              :key="col.key"
              @click.prevent="toggleColumn(col.key)"
            >
              <div class="flex items-center w-full">
                <Lucide :icon="col.visible ? 'CheckSquare' : 'Square'" class="w-4 h-4 mr-2" />
                {{ col.label }}
              </div>
            </Menu.Item>
          </Menu.Items>
        </Menu>

        <Menu v-if="enableExport">
          <Menu.Button :as="Button" variant="outline-secondary" class="bg-white">
            <Lucide icon="FileCheck2" class="stroke-[1.3] w-4 h-4 mr-2" />
            {{ t("common.export") }}
            <Lucide icon="ChevronDown" class="stroke-[1.3] w-4 h-4 ml-2" />
          </Menu.Button>
          <Menu.Items class="w-40 z-50">
            <Menu.Item @click="onExportCsv">
              <Lucide icon="FileText" class="w-4 h-4 mr-2" /> {{ t("common.export_csv") }}
            </Menu.Item>
            <Menu.Item @click="onExportXlsx">
              <Lucide icon="Sheet" class="w-4 h-4 mr-2" /> {{ t("common.export_xlsx") }}
            </Menu.Item>
            <Menu.Item @click="onPrint">
              <Lucide icon="Printer" class="w-4 h-4 mr-2" /> {{ t("common.print") }}
            </Menu.Item>
          </Menu.Items>
        </Menu>

        <slot name="table-actions" />
      </div>
    </div>

    <form
      v-if="enableTableFilter"
      class="flex flex-col sm:flex-row flex-wrap gap-2 items-end border border-dashed border-slate-300/80 rounded-lg p-3 sm:p-4 bg-slate-50/50"
      @submit.prevent
    >
      <div class="w-full sm:w-auto min-w-[140px]">
        <label class="text-xs font-medium text-slate-500 mb-1 block">
          {{ t("common.table_filter_field") }}
        </label>
        <FormSelect v-model="tableFilterField" class="w-full sm:w-44">
          <option value="__all__">{{ t("common.table_filter_all_columns") }}</option>
          <option
            v-for="col in filterableColumns"
            :key="col.key"
            :value="col.key"
          >
            {{ col.label }}
          </option>
        </FormSelect>
      </div>
      <div class="flex-1 min-w-[180px]">
        <label class="text-xs font-medium text-slate-500 mb-1 block">
          {{ t("common.table_filter_value") }}
        </label>
        <FormInput
          v-model="tableFilterValue"
          type="text"
          class="w-full"
          :placeholder="t('common.table_filter_placeholder')"
        />
      </div>
      <Button
        v-if="tableFilterValue"
        type="button"
        variant="outline-secondary"
        class="w-full sm:w-auto"
        @click="resetTableFilter"
      >
        {{ t("common.reset") }}
      </Button>
      <p
        v-if="tableFilterValue.trim() && data.length > 0"
        class="w-full text-xs text-slate-500 sm:mb-2"
      >
        {{
          t("common.table_filter_results", {
            shown: filteredData.length,
            total: data.length,
          })
        }}
      </p>
    </form>

    <div v-if="$slots['header-tools']">
      <slot name="header-tools" />
    </div>

    <div
      :class="[
        'overflow-x-auto',
        maxHeight
          ? 'scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent overflow-y-auto'
          : '',
      ]"
      :style="maxHeight ? { maxHeight } : {}"
    >
      <table
        class="w-full min-w-full bg-white border border-slate-200 divide-y divide-slate-200"
      >
        <thead class="bg-slate-50 border-b border-slate-200">
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              scope="col"
              :class="[
                'px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-50',
                col.sortable ? 'cursor-pointer hover:bg-slate-100 transition-colors' : '',
                maxHeight ? 'sticky top-0 z-10 shadow-[0_1px_0_0_#e2e8f0]' : '',
              ]"
              @click="col.sortable ? onSort(col.key) : undefined"
            >
              <div class="flex items-center gap-2">
                {{ col.label }}
                <div v-if="col.sortable" class="flex flex-col text-slate-400">
                  <Lucide
                    v-if="sortBy === col.key && sortOrder === 'asc'"
                    icon="ChevronUp"
                    class="w-3 h-3 text-primary"
                  />
                  <Lucide
                    v-else-if="sortBy === col.key && sortOrder === 'desc'"
                    icon="ChevronDown"
                    class="w-3 h-3 text-primary"
                  />
                  <Lucide v-else icon="ChevronsUpDown" class="w-3 h-3 opacity-30" />
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-slate-200 bg-white">
          <tr
            v-for="(row, idx) in filteredData"
            :key="(row?.id as string | number) ?? idx"
            class="hover:bg-slate-50"
          >
            <td
              v-for="col in visibleColumns"
              :key="col.key"
              class="px-4 py-3 text-sm text-slate-700"
            >
              <slot :name="'cell-' + col.key" :item="row">
                {{ row?.[col.key] }}
              </slot>
            </td>
          </tr>

          <tr v-if="filteredData.length === 0">
            <td
              :colspan="visibleColumns.length"
              class="px-4 py-6 text-center text-sm text-slate-500"
            >
              {{ resolvedEmptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
